import React, { useState } from 'react';
import {
  Button, IconButton, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, Box,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CreateTriggerPayload } from '../types';

interface CreateTriggerDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateTriggerPayload) => Promise<void>;
}

const CreateTriggerDialog: React.FC<CreateTriggerDialogProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [checkType, setCheckType] = useState('scheduled_time');
  const [name, setName] = useState('');
  const [scheduleType, setScheduleType] = useState<'daily' | 'monthly' | 'once' | 'interval_30m'>('monthly');
  const [scheduleDay, setScheduleDay] = useState<number>(1);
  const [scheduleDate, setScheduleDate] = useState<string>(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [time, setTime] = useState('00:00');
  const [status, setStatus] = useState<'normal' | 'important'>('normal');
  const [template, setTemplate] = useState('');

  const handleCheckTypeChange = (newType: string) => {
    setCheckType(newType);
    if (newType === 'stopped_synchronizer') {
      setScheduleType('interval_30m');
      setStatus('important');
    } else if (newType === 'missing_daily_report') {
      setScheduleType('daily');
      setTime('10:00');
      setStatus('important');
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !template.trim()) return;

    await onSubmit({
      name: name.trim(),
      description: checkType === 'missing_daily_report'
        ? 'Проверка на ввод данных (CheckUsersOnDay)'
        : (checkType === 'stopped_synchronizer'
          ? 'Контроль синхронизатора касс (Остановка службы)'
          : 'Рассылка по дате и времени'),
      checkType,
      scheduleType,
      scheduleDay: scheduleType === 'monthly' ? scheduleDay : null,
      scheduleDate: scheduleType === 'once' ? scheduleDate : null,
      scheduleTime: scheduleType === 'interval_30m' ? '00:00:00' : `${time}:00`,
      status,
      sender: 'Система',
      messageTemplate: template.trim(),
      isEnabled: 1
    });

    // Сброс формы
    setName('');
    setTemplate('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '8px', p: 0.5 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', py: 1, px: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
          Новое правило триггера
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2, px: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.85rem' }}>Триггер *</InputLabel>
          <Select
            value={checkType}
            label="Триггер *"
            onChange={e => handleCheckTypeChange(e.target.value)}
            sx={{ fontSize: '0.85rem' }}
          >
            <MenuItem value="stopped_synchronizer" sx={{ fontSize: '0.85rem' }}>
              Контроль синхронизатора касс (Остановка службы)
            </MenuItem>
            <MenuItem value="scheduled_time" sx={{ fontSize: '0.85rem' }}>
              По дате и времени (авто-рассылка по расписанию)
            </MenuItem>
            <MenuItem value="missing_daily_report" sx={{ fontSize: '0.85rem' }}>
              Проверка на ввод данных (CheckUsersOnDay)
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Название правила *"
          size="small"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Например: Контроль синхронизатора касс"
          sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
        />

        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: '0.85rem' }}>Периодичность *</InputLabel>
          <Select
            value={scheduleType}
            label="Периодичность *"
            onChange={e => setScheduleType(e.target.value as 'daily' | 'monthly' | 'once' | 'interval_30m')}
            sx={{ fontSize: '0.85rem' }}
          >
            <MenuItem value="interval_30m" sx={{ fontSize: '0.85rem' }}>
              Каждые 30 минут (интервал)
            </MenuItem>
            <MenuItem value="daily" sx={{ fontSize: '0.85rem' }}>
              Каждый день (ежедневно)
            </MenuItem>
            <MenuItem value="monthly" sx={{ fontSize: '0.85rem' }}>
              Каждый месяц (в определенный день)
            </MenuItem>
            <MenuItem value="once" sx={{ fontSize: '0.85rem' }}>
              Разово (в конкретную дату)
            </MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {scheduleType === 'monthly' && (
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel sx={{ fontSize: '0.85rem' }}>День месяца *</InputLabel>
              <Select
                value={scheduleDay}
                label="День месяца *"
                onChange={e => setScheduleDay(Number(e.target.value))}
                sx={{ fontSize: '0.85rem' }}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <MenuItem key={day} value={day} sx={{ fontSize: '0.85rem' }}>
                    {day}-го числа
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {scheduleType === 'once' && (
            <TextField
              label="Дата выполнения *"
              type="date"
              size="small"
              value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
              sx={{ minWidth: 150, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
            />
          )}

          {scheduleType !== 'interval_30m' ? (
            <TextField
              label="Время срабатывания *"
              type="time"
              size="small"
              fullWidth
              value={time}
              onChange={e => setTime(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
            />
          ) : (
            <Box sx={{ flexGrow: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', py: 0.8, px: 1.2 }}>
              <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600, fontSize: '0.78rem' }}>
                Каждые 30 мин (в рабочее время аптек)
              </Typography>
            </Box>
          )}

          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel sx={{ fontSize: '0.85rem' }}>Важность *</InputLabel>
            <Select
              value={status}
              label="Важность *"
              onChange={e => setStatus(e.target.value as 'normal' | 'important')}
              sx={{ fontSize: '0.85rem' }}
            >
              <MenuItem value="important" sx={{ fontSize: '0.85rem' }}>
                Важное
              </MenuItem>
              <MenuItem value="normal" sx={{ fontSize: '0.85rem' }}>
                Обычное
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="Текст уведомления *"
          multiline
          rows={3}
          fullWidth
          size="small"
          value={template}
          onChange={e => setTemplate(e.target.value)}
          placeholder="Введите текст уведомления..."
          helperText="Переменные: {date} - дата, {time} - время"
          sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.2, borderTop: '1px solid #e2e8f0' }}>
        <Button size="small" onClick={onClose} sx={{ color: '#64748b', textTransform: 'none', fontSize: '0.8rem' }}>
          Отмена
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!name.trim() || !template.trim()}
          onClick={handleSubmit}
          sx={{
            bgcolor: '#16a34a',
            color: 'white',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            fontSize: '0.8rem',
            px: 2,
            '&:hover': { bgcolor: '#15803d' },
            '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' }
          }}
        >
          Сохранить правило
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTriggerDialog;
